import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

/**
 * Custom hook for folder upload functionality
 * Provides upload state management and upload functions for folder portals
 */
export const useFolderUpload = () => {
  const [uploadingToFolder, setUploadingToFolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const { getAccessTokenSilently, user } = useAuth0();

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  const uploadFilesToFolder = async (folderId, files, refreshDataCallback) => {
    if (!files || files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    try {
      setUploadProgress('Uploading...');
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'create:files',
        },
      });

      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Get author name from user
      const author = user?.nickname?.replace('.', ' ').replace(/\b\w/g, char => char.toUpperCase()) || 'Unknown';
      formData.append('author', author);

      const response = await api.post(`/api/upload-to-folder/${folderId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200) {
        setUploadProgress('Upload successful!');
        
        // Call the refresh callback to update data in parent component
        if (refreshDataCallback) {
          await refreshDataCallback();
        }
        
        // Reset upload state after success
        setTimeout(() => {
          setUploadingToFolder(null);
          setSelectedFiles([]);
          setUploadProgress(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error uploading files to folder:', error);
      if (error.response?.status === 403) {
        setUploadProgress('Access denied. You do not have permission to upload to this folder.');
      } else {
        setUploadProgress('Error uploading files. Please try again.');
      }
      setTimeout(() => setUploadProgress(null), 3000);
    }
  };

  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUploadClick = (folderId) => {
    setUploadingToFolder(folderId);
    setSelectedFiles([]);
    setUploadProgress(null);
  };

  const cancelUpload = () => {
    setUploadingToFolder(null);
    setSelectedFiles([]);
    setUploadProgress(null);
  };

  return {
    uploadingToFolder,
    selectedFiles,
    uploadProgress,
    uploadFilesToFolder,
    handleFileSelection,
    handleUploadClick,
    cancelUpload
  };
};